import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

const MENU_ITEMS = [
  { to: "/account",  label: "Dashboard",        icon: "ri-dashboard-line" },
  { to: "/listings", label: "My Listings",      icon: "ri-store-3-line" },
  { to: "/profile",  label: "Business Profile", icon: "ri-user-settings-line" },
];

export default function UserMenu() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!user) return null;

  const initials = user.name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  function handleLogout() {
    setOpen(false);
    logout();
    navigate("/login");
  }

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Open account menu"
        className="flex items-center gap-2 rounded-full border border-background-300 py-1 pl-1 pr-2.5 transition-colors duration-200 hover:bg-background-100"
      >
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-500 text-xs font-bold text-background-50">
          {initials}
        </span>
        <span className="hidden max-w-[110px] truncate text-xs font-semibold text-foreground-800 sm:block">
          {user.name.split(" ")[0]}
        </span>
        <span className="flex h-4 w-4 items-center justify-center text-foreground-500">
          <i className={open ? "ri-arrow-up-s-line text-base" : "ri-arrow-down-s-line text-base"} />
        </span>
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute right-0 top-[calc(100%+10px)] z-50 w-60 overflow-hidden rounded-xl border border-background-200 bg-background-50"
        >
          <div className="border-b border-background-100 bg-background-100/60 px-4 py-3">
            <p className="truncate text-sm font-semibold text-foreground-950">{user.name}</p>
            <p className="truncate text-xs text-foreground-500">{user.email}</p>
          </div>

          <div className="flex flex-col p-1.5">
            {MENU_ITEMS.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                role="menuitem"
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100 hover:text-foreground-950"
              >
                <span className="flex h-5 w-5 items-center justify-center text-foreground-500">
                  <i className={`${item.icon} text-base`} />
                </span>
                {item.label}
              </Link>
            ))}
          </div>

          <div className="border-t border-background-100 p-1.5">
            <button
              type="button"
              onClick={handleLogout}
              role="menuitem"
              className="flex w-full cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100 hover:text-foreground-950"
            >
              <span className="flex h-5 w-5 items-center justify-center text-foreground-500">
                <i className="ri-logout-box-r-line text-base" />
              </span>
              Sign out
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}